import { z } from 'zod'
import { t } from 'i18next'
import { PRINTABLE_CHARACTERS_REGEXP } from './validation'

const escapeList: Record<string, string> = {
  '\n': '\\n',
  '\r': '\\r',
  '\t': '\\t'
}

/**
 * Find the first character in `value` that does not match `allowed`.
 *
 * Only works with RegExp that satisfy: for any string s,
 * RegExp.test(s) iff for all char c in s, RegExp.test(c).
 * i.e. RegExp may only check for valid characters, not string format.
 * e.g. works with ^[a-zA-ZäöüÄÖÜß]*$, does not work with ^aa\d{8}bb$
 */
export function findFirstInvalidChar(
  value: string,
  allowed: RegExp
): string | null {
  for (const char of value) {
    if (!allowed.test(char)) {
      return char
    }
  }
  return null
}

/**
 * Escape a character for display if it is not a printable character.
 * Returns the original character if printable, otherwise an escape sequence.
 */
export function escapeNonPrintable(char: string): string {
  if (!PRINTABLE_CHARACTERS_REGEXP.test(char)) {
    return (
      escapeList[char] ??
      `\\u${char.codePointAt(0)?.toString(16).toUpperCase().padStart(4, '0')}`
    )
  }
  return char
}

/**
 * Creates a Zod superRefine function that checks each character against `allowedRegExp`.
 * Shows a translated error message with the specific invalid character.
 *
 * Only works with RegExp that satisfy: for any string s,
 * RegExp.test(s) iff for all char c in s, RegExp.test(c).
 * i.e. RegExp may only check for valid characters, not string format.
 * e.g. works with ^[a-zA-ZäöüÄÖÜß]*$, does not work with ^aa\d{8}bb$
 */
export function allowedChars(allowedRegExp: RegExp) {
  return (value: string, ctx: z.RefinementCtx) => {
    const invalidChar = findFirstInvalidChar(value, allowedRegExp)
    if (invalidChar) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('common:validation.charset.specific', {
          character: escapeNonPrintable(invalidChar)
        })
      })
    }
  }
}
